<template>
  <v-row>
    <v-col :cols="12" sm="10" offset-sm="1" md="8" offset-md="2" lg="6" offset-lg="3" xl="4" offset-xl="4">
      <v-card v-if="action === 'language' && placementLevel === ''">
        <v-card-text>
          <p>Loading your language test...</p>
        </v-card-text>
      </v-card>
      <v-card v-else-if="action === 'language'">
        <v-card-text>
          <p>Thank you for completing the {{ language }} test. Your placement level is {{ placementLevel }}, so you should request that course/level when you submit your pre-registration.</p>
          <p v-if="needsLanguageUpload">To help verify you're ready for this placement level, you will need to do an interview with a professor in our language department. We will notify the professor and they will reach out to your Covenant email to schedule that interview.</p>
        </v-card-text>
        <v-card-actions>
          <v-btn to="/">Continue with Scots Connect</v-btn>
        </v-card-actions>
      </v-card>
      <v-card v-else>
        <v-card-text>
          <p>Marking to-do list item as completed...</p>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>
<script>
import { ref, computed, onMounted } from '@vue/composition-api'

/**
 * This is a page where a student is sent once they've completed an external link. A code is included which lets us know which was completed,
 * and we can then verify it and mark it as completed, before redirecting the student back to their dashboard.
 */
export default {
  setup (props, { root }) {
    const user = computed(() => root.$store.state.user)
    const action = computed(() => root.$route.params.action)
    const needsLanguageUpload = ref(false)
    const language = ref('')
    const placementLevel = ref('')

    onMounted(() => process())

    async function process () {
      if (root.$store.state.tracker.id === '') {
        setTimeout(() => process(), 100)
        return
      }
      let index = 0
      console.log(action.value)
      switch (action.value) {
        case 'language':
          // Retrieve their score information and update their stored record
          const { data } = await root.$feathers.service('orientation/language-placement').find({ query: { pidm: user.value.pidm } })
          if (data.length > 0) {
            const scoreData = await root.$feathers.service('orientation/language-placement').find({ query: { report: 'scores' } })
            if ('scoreReports' in scoreData) {
              // Find any scores that exist here that match the assessmentId for one that we have and update their scores
              for (const { userAssessmentId, status, score, placement: pl } of scoreData.scoreReports) {
                if (status !== 'Completed') continue
                let placement = pl
                // Check to see if the returned placement is only numbers; if so, then add the first 3-letters of the language as a subject prefex
                // i.e. if it comes in as 102 for Spanish, then make it SPA102. If it comes in as 201 for French, make it FRE201.
                // One exception is if it comes in as 300. In this case, make it "300-level Spanish course"
                const rec = data.find(({ userAssessmentId: uId }) => uId === userAssessmentId)
                if (rec) {
                  language.value = rec.language
                  if (/^[0-9]{3}$/.test(pl)) {
                    if (pl === '300') placement = `300-level ${language.value} course`
                    else placement = language.value.substring(0, 3).toUpperCase() + pl
                  }
                  placementLevel.value = placement
                  await root.$feathers.service('orientation/language-placement').patch(rec._id, { score, placement })
                  // Then mark it as completed in their checklist
                  index = root.$store.state.actions.findIndex(({ isLanguageTest }) => isLanguageTest)
                  if (index >= 0) {
                    let { actions } = await root.$feathers.service('orientation/tracker').patch(root.$store.state.tracker.id, { ['actions.' + index + '.done']: true })
                    if (parseInt(placement.replaceAll(/[^0-9]/ig, '')) > 200) {
                      needsLanguageUpload.value = true
                      await root.$feathers.service('system/email').create({ from: 'report-email@covenant.edu', to: (language.value === 'French' ? 'bryant.white@covenant.edu' : ['brianne.kobeck@covenant.edu', 'deborah.forteza@covenant.edu']), subject: user.value.name + ' Needs Interview for ' + language.value, html: `<p>${user.value.name} tested into ${(pl === '300' ? 'a ' : '') + placement}. This is your prompt to reach out to schedule an interview to ensure they are ready for that placement level.</p>` })
                    }
                    root.$store.commit('setActions', actions)
                  }
                }
              }
            }
          }
          break
        case 'latin':
          index = root.$store.state.actions.findIndex(({ isLanguageTest }) => isLanguageTest)
          if (index >= 0) {
            let { actions } = await root.$feathers.service('orientation/tracker').patch(root.$store.state.tracker.id, { ['actions.' + index + '.done']: true })
            root.$store.commit('setActions', actions)
            root.$store.dispatch('snackbar', { active: true, color: 'success', timeout: 6000, text: 'Your language test was marked as completed on your to-do list.' })
          }
          if (!needsLanguageUpload.value) root.$router.replace('/')
          break
        default:
          const regex = new RegExp(action.value, 'ig')
          index = root.$store.state.actions.findIndex(({ title }) => regex.test(title))
          if (index >= 0) {
            const { title } = root.$store.state.actions[index]
            let { actions } = await root.$feathers.service('orientation/tracker').patch(root.$store.state.tracker.id, { ['actions.' + index + '.done']: true })
            root.$store.commit('setActions', actions)
            root.$store.dispatch('snackbar', { active: true, color: 'success', timeout: 6000, text: `${title} was marked as completed on your to-do list.` })
          } else {
            root.$store.dispatch('snackbar', { active: true, color: 'error', timeout: 10000, text: 'No matching item was found in your to-do list to mark completed. You will need to dismiss it manually using the three-dot menu.' })
          }
          if (!needsLanguageUpload.value) root.$router.replace('/')
          break
      }
    }
    return {
      user,
      action,
      language,
      placementLevel,
      needsLanguageUpload
    }
  }
}
</script>
